import React from "react";
import { Clock, CheckCircle, ArrowRight } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../ui/card";
import { useInteractionTracking } from "../../hooks/useAnalytics";
import welcomeImg from "../../assets/images/welcome-img.svg";

const RefinancingWelcomeScreen = ({ userData, onComplete, onReset }) => {
  const { trackButtonClick } = useInteractionTracking();

  // Refinancing-specific journey steps
  const refinanceSteps = [
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Review your verified profile",
      description: "Confirm your existing financial details",
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Confirm a few quick details",
      description: "Make sure your income and property info is up-to-date",
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Check potential savings",
      description: "See how refinancing could lower your payment",
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Explore options from trusted lenders",
      description: "Compare refinance offers tailored to your profile",
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      title: "Earn from your Ownli refinance profile",
      description: "Get rewarded for your refinancing journey",
    },
  ];

  return (
    <div className="space-y-8 p-4 pt-5 pb-6">
      {/* Refinancing Header */}
      <div className="text-center bg-white p-5 rounded-[20px]">
        <h1 className="font-bebasNeue text-3xl font-bold text-gray-900 mb-2">
          See if refinancing can lower your payment
        </h1>
        <p className="text-lg text-gray-600">
          You&apos;re in a great window to compare refinancing options. Since your profile
          is already verified with Ownli, this will be quick.
        </p>
        <img
          className="mx-auto mt-5 max-w-[250px]"
          src={welcomeImg}
          alt="Refinancing illustration"
        />
      </div>

      {/* Time Commitment Card */}
      <Card className="bg-greenish border-none !mt-6 rounded-[20px]">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <Clock className="min-w-[25px] min-h-[25px] text-green" />
            <p className="text-base text-darkGrey">
              Takes less than 3 minutes to check your refinance options
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Refinancing Process Steps */}
      <Card className="bg-white border-none rounded-[20px] !mt-0">
        <CardHeader>
          <CardTitle className="text-base font-bold">
            Refinancing process:
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {refinanceSteps.map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                <span className="mt-2 min-w-[10px] min-h-[10px] bg-darkGreen rounded-full"></span>
                <div>
                  <h3 className="font-base text-darkGrey">{step.title}</h3>
                  {/* Keeping description here for future use if design needs it */}
                  {/* <p className="text-sm text-gray-500">{step.description}</p> */}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* CTAs */}
      <div className="space-y-3">
        <button
          onClick={() => {
            trackButtonClick("refi_get_started", {
              button_text: "START NOW",
            });
            onComplete();
          }}
          className="w-full flex uppercase font-bold items-center justify-center gap-2 bg-purpleGradient text-white px-4 py-3 rounded-lg hover:bg-purpleGradientDark transition-colors"
        >
          START NOW
          <ArrowRight className="w-4 h-4" />
        </button>

        <div className="text-center text-sm text-gray-600">
          By continuing, you agree to Ownli&apos;s{" "}
          <a
            href="https://support.ownli.co/hc/en-us/articles/360011275958-Terms-of-Service"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline inline-flex items-center"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="https://support.ownli.co/hc/en-us/articles/360011276138-Ownli-Privacy-Policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 underline inline-flex items-center"
          >
            Privacy Policy
          </a>{" "}
          .
        </div>
      </div>
    </div>
  );
};

export default RefinancingWelcomeScreen;
